import Select from "components/ui/Select";
import Input from "components/ui/Input";
import Switcher from "components/ui/Switcher";
import SelectAreas from "components/ui/selectors/SelectAreas";
import SelectGroups from "components/ui/selectors/SelectGroups";
import useManageGLAccountMastForm, {
  accountTypes,
} from "hooks/useManageGLAccountMastForm";

export default function ManageGLAccountMastForm({
  ps_Id = null,
  initialData = null,
  onSubmit,
  onCancel,
  isLoading: externalLoading = false,
}) {
  const {
    formData,
    setFormData,
    selectedType,
    selectedState,
    selectedArea,
    isEditMode,
    isForeignExport,
    isIndianAddress,
    isForeignAddress,
    showDetails,
    setShowDetails,
    stateOptions,
    countryOptions,
    isCountriesLoading,
    isStatesLoading,
    isLoadingExisting,
    isLoading,
    isMutating,
    handleInputChange,
    handleTypeChange,
    handleStateChange,
    handleCountryChange,
    handleAreaChange,
    handleGSTToggle,
    handleSubmit,
  } = useManageGLAccountMastForm({
    ps_Id,
    initialData,
    onSubmit,
    externalLoading,
  });
  // Show loading skeleton while fetching existing data for edit mode
  if (isEditMode && isLoadingExisting) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          {/* Loading skeleton for form */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="h-16 bg-gray-200 rounded"></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
            <div className="h-16 bg-gray-200 rounded"></div>
            <div className="h-16 bg-gray-200 rounded"></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
            <div className="h-16 bg-gray-200 rounded"></div>
            <div className="h-16 bg-gray-200 rounded"></div>
          </div>
          <div className="mt-6">
            <div className="h-8 bg-gray-200 rounded w-48"></div>
          </div>
          <div className="flex justify-end gap-3 pt-6 border-t border-gray-200 mt-6">
            <div className="h-10 bg-gray-200 rounded w-20"></div>
            <div className="h-10 bg-gray-200 rounded w-32"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Loading overlay for form submission */}
      {isMutating && (
        <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10 rounded-lg">
          <div className="flex items-center space-x-2">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
            <span className="text-sm font-medium text-gray-900">
              {isEditMode ? "Updating account..." : "Creating account..."}
            </span>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Account Type - First field */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Select
            value={selectedType}
            onChange={handleTypeChange}
            options={accountTypes}
            label="Account Type"
            placeholder="Select account type"
            isSearchable={false}
            required
          />
        </div>

        {/* Account Name and Code */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            label="Account Name"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            placeholder="Enter account name"
            maxLength={40}
            isRequired={true}
          />

          <Input
            label="Account Code"
            name="ps_code"
            value={formData.ps_code}
            onChange={handleInputChange}
            placeholder="Enter account code"
            maxLength={5}
            isRequired={true}
            disabled={isEditMode}
            helperText={
              isEditMode
                ? `Account ID: ${formData.agvct}${formData.ps_code}`
                : `Final Account ID: ${formData.agvct}${formData.ps_code}`
            }
          />
        </div>

        {/* Country, State and Account Group */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {isForeignExport && (
            <Select
              label="Country"
              value={countryOptions.find(
                (country) => country.value === formData.ps_country
              )}
              onChange={handleCountryChange}
              options={countryOptions}
              placeholder="Select country"
              isLoading={isCountriesLoading}
              isRequired={true}
              isClearable={false}
              isSearchable={true}
            />
          )}
          {isIndianAddress ? (
            <Select
              value={selectedState}
              onChange={handleStateChange}
              options={stateOptions}
              label="State"
              placeholder="Select state"
              isSearchable={true}
              isLoading={isStatesLoading}
              isRequired={true}
            />
          ) : (
            <Input
              label="State / Province"
              name="ps_state"
              value={formData.ps_state}
              onChange={handleInputChange}
              placeholder="Enter state or province"
              maxLength={40}
            />
          )}

          {/* Account Group only for Customers and Vendors */}
          {(formData.agvct === "C" || formData.agvct === "V") && (
            <SelectGroups
              value={formData.select_grptype}
              onChange={(grpId) => {
                setFormData((prev) => ({ ...prev, select_grptype: grpId || "" }));
              }}
              type={formData.agvct}
              label="Account Group"
              showAddButton={true}
            />
          )}
        </div>

        {/* Area - only for Customers */}
        {formData.agvct === "C" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <SelectAreas
              value={selectedArea}
              onChange={handleAreaChange}
              label="Area"
              placeholder="Select area (optional)"
              showAddButton={true}
              isClearable={true}
            />
          </div>
        )}

        {/* Details Toggle */}
        <div className="border-t border-gray-200 pt-6">
          <Switcher
            enabled={showDetails}
            onChange={setShowDetails}
            label="Show Additional Details"
            description="Address, contact information, and financial details"
          />
        </div>

        {/* Additional Details Section */}
        {showDetails && (
          <div className="space-y-6 border border-gray-200 rounded-lg p-6 bg-gray-50">
            {/* Address Section */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Address Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Input
                  label="Address Line 1"
                  name="psadd1"
                  value={formData.psadd1}
                  onChange={handleInputChange}
                  placeholder="Address Line 1"
                  maxLength={40}
                />
                <Input
                  label="Address Line 2"
                  name="psadd2"
                  value={formData.psadd2}
                  onChange={handleInputChange}
                  placeholder="Address Line 2"
                  maxLength={40}
                />
                <Input
                  label="Address Line 3"
                  name="psadd3"
                  value={formData.psadd3}
                  onChange={handleInputChange}
                  placeholder="Enter Address Line 3"
                  maxLength={40}
                />
                <Input
                  label="City"
                  name="ps_city"
                  value={formData.ps_city}
                  onChange={handleInputChange}
                  placeholder="City"
                  maxLength={20}
                />
                <Input
                  label="Pincode"
                  name="ps_pin"
                  type="number"
                  value={formData.ps_pin}
                  onChange={handleInputChange}
                  placeholder="Enter Pincode"
                />
                <Input
                  label="Contact Person"
                  name="ps_contactperson"
                  value={formData.ps_contactperson}
                  onChange={handleInputChange}
                  placeholder="Contact Person"
                  maxLength={40}
                />
              </div>
            </div>

            {/* Phone Numbers */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Phone Numbers
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Input
                  label="Phone No. (O)"
                  name="ps_contactno"
                  value={formData.ps_contactno}
                  onChange={handleInputChange}
                  placeholder="Phone No. (O)"
                  maxLength={11}
                />
                <Input
                  label="Phone No. (R)"
                  name="ps_contactres"
                  value={formData.ps_contactres}
                  onChange={handleInputChange}
                  placeholder="Phone No. (R)"
                  maxLength={11}
                />
                <Input
                  label="Mobile No. 1"
                  name="ps_mobile1"
                  value={formData.ps_mobile1}
                  onChange={handleInputChange}
                  placeholder="Mobile No. 1"
                  maxLength={11}
                />
                <Input
                  label="Mobile No. 2"
                  name="ps_mobile2"
                  value={formData.ps_mobile2}
                  onChange={handleInputChange}
                  placeholder="Mobile No.2"
                  maxLength={11}
                />
              </div>
            </div>

            {/* General Section */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                General
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Input
                  label="Whatsapp No."
                  name="ps_wtsno"
                  value={formData.ps_wtsno}
                  onChange={handleInputChange}
                  placeholder="Whatsapp No."
                  maxLength={11}
                />
                <Input
                  label="E-Mail Address"
                  name="ps_emailid"
                  type="email"
                  value={formData.ps_emailid}
                  onChange={handleInputChange}
                  placeholder="E-Mail Address"
                  maxLength={100}
                />
                <Input
                  label="Copy E-Mail Address"
                  name="ps_emailid1"
                  type="email"
                  value={formData.ps_emailid1}
                  onChange={handleInputChange}
                  placeholder="Copy E-Mail Address"
                  maxLength={100}
                />
              </div>
            </div>

            {/* Order & Sales Section */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Order & Sales
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Input
                  label="Whatsapp No."
                  name="sales_contactnowts"
                  value={formData.sales_contactnowts}
                  onChange={handleInputChange}
                  placeholder="Whatsapp No."
                  maxLength={10}
                />
                <Input
                  label="E-Mail Address"
                  name="sales_email"
                  type="email"
                  value={formData.sales_email}
                  onChange={handleInputChange}
                  placeholder="E-Mail Address"
                  maxLength={100}
                />
                <Input
                  label="Copy E-Mail Address"
                  name="sales_email1"
                  type="email"
                  value={formData.sales_email1}
                  onChange={handleInputChange}
                  placeholder="Copy E-Mail Address"
                  maxLength={100}
                />
              </div>
            </div>

            {/* Accounts Section */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Accounts
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Input
                  label="Whatsapp No."
                  name="acc_contactnowts"
                  value={formData.acc_contactnowts}
                  onChange={handleInputChange}
                  placeholder="Whatsapp No."
                  maxLength={10}
                />
                <Input
                  label="E-Mail Address"
                  name="acc_email"
                  type="email"
                  value={formData.acc_email}
                  onChange={handleInputChange}
                  placeholder="E-Mail Address"
                  maxLength={100}
                />
                <Input
                  label="Copy E-Mail Address"
                  name="acc_email1"
                  type="email"
                  value={formData.acc_email1}
                  onChange={handleInputChange}
                  placeholder="Copy E-Mail Address"
                  maxLength={100}
                />
              </div>
            </div>

            {/* Scheme Information */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Scheme Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Input
                  label="Scheme WhatsApp No."
                  name="scheme_contactnowts"
                  value={formData.scheme_contactnowts}
                  onChange={handleInputChange}
                  placeholder="Scheme WhatsApp No."
                  maxLength={10}
                />
                <Input
                  label="Scheme Order Email"
                  name="ps_scheme_order_email"
                  type="email"
                  value={formData.ps_scheme_order_email}
                  onChange={handleInputChange}
                  placeholder="Scheme Order Email"
                  maxLength={100}
                />
              </div>
            </div>

            {/* Tax and Financial Information */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Tax & Financial Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {!isForeignAddress && (
                  <>
                    <Input
                      label="GST No."
                      name="ps_gstno"
                      value={formData.ps_gstno}
                      onChange={handleInputChange}
                      placeholder="GST No."
                      maxLength={20}
                    />
                    <Input
                      label="PAN No."
                      name="ps_panno"
                      value={formData.ps_panno}
                      onChange={handleInputChange}
                      placeholder="PAN No."
                      maxLength={15}
                    />
                    <Input
                      label="TCS Percentage"
                      name="tcsperc"
                      type="number"
                      step="0.000001"
                      value={formData.tcsperc}
                      onChange={handleInputChange}
                      placeholder="TCS Percentage (0-100)"
                      helperText="Up to 6 decimal places, range 0-100%"
                    />
                  </>
                )}
                <Input
                  label="Transaction Limit"
                  name="transaction_limit"
                  type="number"
                  step="0.01"
                  value={formData.transaction_limit}
                  onChange={handleInputChange}
                  placeholder="Transaction Limit (0 is infinite)"
                  helperText="0 for unlimited transaction limit"
                />
              </div>

              {!isForeignAddress && (
                <div className="mt-4">
                  <Switcher
                    enabled={formData.gst}
                    onChange={handleGSTToggle}
                    label="GST Applicable"
                    description="Enable if GST is applicable for this account"
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {/* Form Actions */}
        <div className="flex justify-end gap-3 pt-6 border-t border-gray-200">
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
              disabled={isLoading}
            >
              Cancel
            </button>
          )}
          <button
            type="submit"
            disabled={isLoading}
            className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            {isMutating && (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            )}
            <span>
              {isLoading
                ? isEditMode
                  ? "Updating..."
                  : "Creating..."
                : isEditMode
                ? "Update Account"
                : "Create Account"}
            </span>
          </button>
        </div>
      </form>
    </div>
  );
}
